import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";
import { useTheme } from "../../../context/ThemeContext";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { sendForgotPasswordEmail } from "../../../utils/sendForgotPasswordEmail";

const ResetPassword = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleCloseSuccess = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSuccess(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!executeRecaptcha) return;
    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha("reset_password");
    } catch {
      setAlertMessage("reCAPTCHA verification failed. Please try again.");
      setOpen(true);
      return;
    }
    setLoading(true);

    const formData = new FormData(e.target);
    const emailAddress = formData.get("email");

    try {
      const { isEmailSent } = await sendForgotPasswordEmail(
        emailAddress,
        recaptchaToken
      );

      if (isEmailSent) {
        setOpenSuccess(true);
        setTimeout(() => {
          navigate("/");
        }, 5000);
      } else {
        setAlertMessage(
          "Account associated with this email could not be found."
        );
        setOpen(true);
      }
    } catch (error) {
      setAlertMessage("An error occurred while sending the recovery email.");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%" }}>
      <Snackbar
        sx={{ zIndex: 9999999 }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        data-testid="reset-password--Snackbar-0"
      >
        <Alert
          onClose={handleClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
          data-testid="reset-password--Alert-0"
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        sx={{ zIndex: 9999999 }}
        open={openSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        data-testid="reset-password--Snackbar-1"
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
          data-testid="reset-password--Alert-1"
        >
          Success! Please check your inbox
        </Alert>
      </Snackbar>

      <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
        Reset your password
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Enter email address linked to your account and we will send you a password recovery link.
      </Typography>

      <Box component="form" onSubmit={handleFormSubmit}>
        <TextField
          fullWidth
          label="Email Address"
          name="email"
          type="email"
          required
          inputProps={{ autoComplete: "off" }}
          sx={{ mb: 3 }}
          data-testid="reset-password--TextField-0"
        />

        <Button
          type="submit"
          variant="outlined"
          className="btn-glass"
          disabled={loading}
          fullWidth
          sx={{ mb: 3 }}
          data-testid="reset-password--Button-0"
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Send Recovery Email"}
        </Button>

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Remember password?
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="text"
            sx={{
              p: 0,
              minWidth: 0,
              fontWeight: 600,
              fontSize: 14,
              color: "primary.main",
              "&:hover": { background: "transparent", color: "primary.dark" },
            }}
            data-testid="reset-password--Link-0"
          >
            Login
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <ThemeToggle
            titlefontSize={16}
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
